// View menu component
// Side bar for switching between views / changing graph coloring

import React from "react";
// Components
import SearchBars from "./searchBars";
// Context
import { makeStyles } from "@material-ui/core/styles";
import { ViewContext } from "../../context/view/ViewContext";
import GaugeContext from "../../context/gauge/gaugeContext";
// Material UI
import Button from "@material-ui/core/Button";
import List from "@material-ui/core/List";
import Divider from "@material-ui/core/Divider";
import ListItem from "@material-ui/core/ListItem";
import Drawer from "@material-ui/core/Drawer";
import Checkbox from "@material-ui/core/Checkbox";
import Switch from "@material-ui/core/Switch";
import Grid from "@material-ui/core/Grid";
import { withStyles } from "@material-ui/core/styles";
import { blue, red } from "@material-ui/core/colors";

const useStyles = makeStyles((theme) => ({
	root: {
		display: "flex",
		background: "none",
	},
	paper: {
		marginRight: theme.spacing(2),
	},
	grow: {
		zIndex: 10,
	},
	closed: {
		background: "gray",
		zIndex: 10,
	},
	selected: {
		background: "rgb(106, 130, 255)",
	},
}));

const RedBlueSwitch = withStyles({
	switchBase: {
		color: red[300],
		"&$checked": {
			color: blue[500],
		},

		"&$checked + $track": {
			backgroundColor: blue[500],
		},
	},
	checked: {
		color: red[500],
	},
	track: {
		backgroundColor: red[500],
	},
})(Switch);

function useOutsideAlerter(ref, setOpen) {
	React.useEffect(() => {
		/**
		 * Alert if clicked on outside of element
		 */
		function handleClickOutside(event) {
			if (ref.current && !ref.current.contains(event.target)) {
				setOpen(false);
			}
		}

		// Bind the event listener
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			// Unbind the event listener on clean up
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [ref, setOpen]);
}

const ViewMenu = () => {
	const gaugeContext = React.useContext(GaugeContext);
	const { colorBy, setColor, nodeColor, setNodeColor } = gaugeContext;
	const classes = useStyles();
	const [open, setOpen] = React.useState(false);
	const wrapperRef = React.useRef(null);
	useOutsideAlerter(wrapperRef, setOpen);

	const toggleDrawer = (status) => (event) => {
		if (
			event.type === "keydown" &&
			(event.key === "Tab" || event.key === "Shift")
		) {
			return;
		}

		setOpen({ open: !open });
	};

	const list = (
		toggleViews,
		views,
		yAxisCoordinates,
		setYAxisCoordinates
	) => {
		return (
			<div>
				<List className={classes.fullList}>
					<ListItem>
						<Checkbox
							inputProps={{ "aria-label": "primary checkbox" }}
							onChange={() => toggleViews("clusterHierarchy")}
							name={"clusterHierarchy"}
							checked={views.clusterHierarchy}
						/>
						Hierarchy
					</ListItem>
					<ListItem
						button
						className={
							views.applications ? classes.grow : classes.closed
						}
						onClick={() => toggleViews("applications")}
						variant="outlined"
					>
						<Checkbox
							inputProps={{ "aria-label": "primary checkbox" }}
							onChange={() => toggleViews("applications")}
							name={"applications"}
							checked={views.applications}
						/>
						Users and Applications
					</ListItem>
					<ListItem>
						<Checkbox
							inputProps={{ "aria-label": "primary checkbox" }}
							onChange={() => toggleViews("logarithmicFeatures")}
							name={"logarithmicFeatures"}
							checked={views.logarithmicFeatures}
						/>
						Logarithmic Features
					</ListItem>
					<ListItem>
						<Checkbox
							inputProps={{ "aria-label": "primary checkbox" }}
							onChange={() => toggleViews("ratioFeatures")}
							name={"ratioFeatures"}
							checked={views.ratioFeatures}
						/>
						Ratio Features
					</ListItem>
					<ListItem>
						<Checkbox
							inputProps={{ "aria-label": "primary checkbox" }}
							onChange={() => toggleViews("scatterPlots")}
							name={"scatterPlots"}
							checked={views.scatterPlots}
						/>
						Accesses Plots
					</ListItem>
				</List>
				<Divider />
				<List>
					<ListItem>Hierarchy Color Options</ListItem>
					<ListItem>
						<Grid
							component="label"
							container
							alignItems="center"
							spacing={1}
						>
							<Grid item>Cluster Epsilon</Grid>
							<Grid item>
								<RedBlueSwitch
									checked={nodeColor === "size"}
									onChange={() =>
										setNodeColor(
											nodeColor === "size"
												? "epsilon"
												: "size"
										)
									}
								/>
							</Grid>
							<Grid item>Cluster Size</Grid>
						</Grid>
					</ListItem>
					<SearchBars
						selected={classes.selected}
						grow={classes.grow}
					/>
				</List>
				<Divider />
				<List>
					<ListItem>Graph Color Options</ListItem>
					<ListItem>
						<Grid
							component="label"
							container
							alignItems="center"
							spacing={1}
						>
							<Grid item>Users</Grid>
							<Grid item>
								<RedBlueSwitch
									checked={colorBy === "app"}
									onChange={() =>
										setColor(
											colorBy === "app" ? "user" : "app"
										)
									}
								/>
							</Grid>
							<Grid item>Applications</Grid>
						</Grid>
					</ListItem>
				</List>
				<Divider />
				<List>
					<ListItem>Y-Axis Grouping:</ListItem>
					<ListItem>
						<Grid
							component="label"
							container
							alignItems="center"
							spacing={1}
						>
							<Grid item>Per-Column</Grid>
							<Grid item>
								<RedBlueSwitch
									checked={yAxisCoordinates === "unified"}
									onChange={() =>
										setYAxisCoordinates(
											yAxisCoordinates === "per-column"
												? "unified"
												: "per-column"
										)
									}
								/>
							</Grid>
							<Grid item>Unified</Grid>
						</Grid>
					</ListItem>
				</List>
				<Divider />
				<List>
					<ListItem button onClick={() => setOpen(false)}>
						Close
					</ListItem>
				</List>
			</div>
		);
	};

	return (
		<ViewContext.Consumer>
			{({
				toggleViews,
				views,
				yAxisCoordinates,
				setYAxisCoordinates,
			}) => (
				<div>
					<Button
						onClick={() => setOpen(true)}
						style={{ background: "white" }}
					>
						View
					</Button>
					<Drawer
						anchor={"right"}
						open={open}
						onClose={() => setOpen(false)}
						ModalProps={{
							onBackdropClick: () => toggleDrawer(false),
						}}
						transitionDuration={{ enter: 0, exit: 0 }}
					>
						{list(
							toggleViews,
							views,
							yAxisCoordinates,
							setYAxisCoordinates
						)}
					</Drawer>
				</div>
			)}
		</ViewContext.Consumer>
	);
};

export default ViewMenu;
